import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, X, Play, Pause, Archive, Trash2, ChevronDown, ChevronUp,
  Sparkles, Trophy, ClipboardList, BarChart3, Upload, FileJson, Eye, Lock,
  Truck, CheckCircle
} from 'lucide-react';
import { API } from '../config';
import { ImageUpload } from '../components/ImageUpload';

const playfairFont = "'Playfair Display', serif";
const inputStyle = { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };
const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' };
const goldBtn = { background: '#D4AF37', color: '#080C0A' };
const ghostBtn = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };

const STATUS_COLORS = {
  draft: { bg: 'rgba(163,184,172,0.2)', fg: '#A3B8AC' },
  active: { bg: 'rgba(34,197,94,0.2)', fg: '#22C55E' },
  paused: { bg: 'rgba(234,179,8,0.2)', fg: '#EAB308' },
  sold_out: { bg: 'rgba(239,68,68,0.2)', fg: '#EF4444' },
  archived: { bg: 'rgba(100,116,139,0.2)', fg: '#94A3B8' },
};

const TIER_COLORS = {
  gold: '#D4AF37',
  silver: '#C0C0C0',
  bronze: '#CD7F32',
  common: '#A3B8AC',
  last: '#EF4444',
};

const fmt = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);

// ---------- Sub-tabs ----------
const SUB_TABS = [
  { id: 'events', label: 'Events', icon: Sparkles },
  { id: 'shipping', label: 'Shipping', icon: Truck },
  { id: 'economy', label: 'Economy', icon: BarChart3 },
  { id: 'audit', label: 'Audit Log', icon: ClipboardList },
];

export const KujiAdminPanel = () => {
  const [subTab, setSubTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [economy, setEconomy] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [shipping, setShipping] = useState([]);
  const [shippingStatusFilter, setShippingStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/kuji/events`, { withCredentials: true });
      setEvents(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load events');
    }
  }, []);

  const fetchEconomy = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/kuji/economy`, { withCredentials: true });
      setEconomy(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load economy');
    }
  }, []);

  const fetchAudit = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/kuji/audit-log?limit=200`, { withCredentials: true });
      setAuditLog(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit log');
    }
  }, []);

  const fetchShipping = useCallback(async () => {
    try {
      const params = shippingStatusFilter ? { status: shippingStatusFilter } : {};
      const { data } = await axios.get(`${API}/admin/kuji/shipping/requests`, { withCredentials: true, params });
      setShipping(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load shipping requests');
    }
  }, [shippingStatusFilter]);

  useEffect(() => {
    setLoading(true);
    const run = async () => {
      if (subTab === 'events') await fetchEvents();
      else if (subTab === 'economy') await fetchEconomy();
      else if (subTab === 'audit') await fetchAudit();
      else if (subTab === 'shipping') await fetchShipping();
      setLoading(false);
    };
    run();
  }, [subTab, fetchEvents, fetchEconomy, fetchAudit, fetchShipping]);

  return (
    <div data-testid="kuji-admin-panel">
      {/* Sub-tab nav */}
      <div className="flex gap-3 mb-6 overflow-x-auto hide-scrollbar">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          const active = subTab === t.id;
          return (
            <motion.button
              key={t.id}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSubTab(t.id)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap"
              style={{
                background: active ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${active ? '#D4AF37' : 'rgba(255,255,255,0.1)'}`,
                color: active ? '#D4AF37' : '#F8FAF9'
              }}
              data-testid={`kuji-subtab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </motion.button>
          );
        })}
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block w-10 h-10 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
        </div>
      )}

      {!loading && subTab === 'events' && <EventsTab events={events} reload={fetchEvents} />}
      {!loading && subTab === 'shipping' && (
        <ShippingTab
          items={shipping}
          statusFilter={shippingStatusFilter}
          setStatusFilter={setShippingStatusFilter}
          reload={fetchShipping}
        />
      )}
      {!loading && subTab === 'economy' && <EconomyTab economy={economy} />}
      {!loading && subTab === 'audit' && <AuditTab auditLog={auditLog} />}
    </div>
  );
};

// ============================================================
//  EVENTS TAB
// ============================================================
const EventsTab = ({ events, reload }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState(null);

  return (
    <>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h2 className="text-2xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Kuji Events</h2>
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowCreate((v) => !v)}
          className="flex items-center gap-2 px-6 py-3 rounded-full font-medium"
          style={goldBtn}
          data-testid="kuji-new-event-btn"
        >
          {showCreate ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          {showCreate ? 'Cancel' : 'New Event'}
        </motion.button>
      </div>

      <AnimatePresence>
        {showCreate && (
          <EventCreateForm
            onCreated={() => { setShowCreate(false); reload(); }}
          />
        )}
      </AnimatePresence>

      {events.length === 0 ? (
        <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={cardStyle}>
          <Sparkles className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
          <p style={{ color: '#A3B8AC' }}>No Kuji events yet. Create one to begin.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((ev) => (
            <EventCard
              key={ev.id}
              event={ev}
              expanded={expandedEvent === ev.id}
              onToggle={() => setExpandedEvent(expandedEvent === ev.id ? null : ev.id)}
              reload={reload}
            />
          ))}
        </div>
      )}
    </>
  );
};

// ---------- Create Form ----------
const EventCreateForm = ({ onCreated }) => {
  const [form, setForm] = useState({
    name: '', slug: '', description: '', banner_url: '', cover_url: '',
    coin_price_per_draw: '', featured: false, featured_order: 0, category: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.coin_price_per_draw) {
      toast.error('Name and coin price are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/kuji/events`, {
        name: form.name.trim(),
        slug: form.slug.trim() || form.name.trim(),
        description: form.description,
        banner_url: form.banner_url,
        cover_url: form.cover_url,
        coin_price_per_draw: parseInt(form.coin_price_per_draw, 10),
        featured: form.featured,
        featured_order: parseInt(form.featured_order, 10) || 0,
        category: (form.category || '').trim(),
      }, { withCredentials: true });
      toast.success('Event created as draft');
      onCreated();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      onSubmit={submit}
      className="rounded-2xl p-6 mb-6 backdrop-blur-xl grid md:grid-cols-2 gap-4"
      style={cardStyle}
      data-testid="kuji-event-create-form"
    >
      <input
        type="text"
        placeholder="Event Name *"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="rounded-lg px-4 py-3 outline-none"
        style={inputStyle}
        data-testid="kuji-event-name-input"
        required
      />
      <input
        type="text"
        placeholder="Slug (auto if empty)"
        value={form.slug}
        onChange={(e) => setForm({ ...form, slug: e.target.value })}
        className="rounded-lg px-4 py-3 outline-none"
        style={inputStyle}
        data-testid="kuji-event-slug-input"
      />
      <textarea
        placeholder="Description"
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        className="md:col-span-2 rounded-lg px-4 py-3 outline-none"
        rows="2"
        style={inputStyle}
        data-testid="kuji-event-desc-input"
      />
      <input
        type="number"
        placeholder="Coin Price per Draw *"
        value={form.coin_price_per_draw}
        onChange={(e) => setForm({ ...form, coin_price_per_draw: e.target.value })}
        className="rounded-lg px-4 py-3 outline-none"
        style={inputStyle}
        data-testid="kuji-event-price-input"
        required
        min="1"
      />
      <input
        type="number"
        placeholder="Featured Order (lower = first)"
        value={form.featured_order}
        onChange={(e) => setForm({ ...form, featured_order: e.target.value })}
        className="rounded-lg px-4 py-3 outline-none"
        style={inputStyle}
      />
      <input
        type="text"
        placeholder="Category (e.g. Anime, Sneaker, Watch)"
        value={form.category}
        onChange={(e) => setForm({ ...form, category: e.target.value })}
        className="md:col-span-2 rounded-lg px-4 py-3 outline-none"
        style={inputStyle}
        data-testid="kuji-event-category-input"
      />
      <div className="md:col-span-1">
        <ImageUpload
          value={form.banner_url}
          onChange={(v) => setForm({ ...form, banner_url: v })}
          label="Banner Image"
          testId="kuji-event-banner-upload"
        />
      </div>
      <div className="md:col-span-1">
        <ImageUpload
          value={form.cover_url}
          onChange={(v) => setForm({ ...form, cover_url: v })}
          label="Cover Image"
          testId="kuji-event-cover-upload"
        />
      </div>
      <label className="flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer md:col-span-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
        <input
          type="checkbox"
          checked={form.featured}
          onChange={(e) => setForm({ ...form, featured: e.target.checked })}
          className="w-5 h-5 accent-yellow-500"
          data-testid="kuji-event-featured-checkbox"
        />
        <span style={{ color: '#F8FAF9' }}>Mark as Featured (shown on homepage)</span>
      </label>
      <button
        type="submit"
        disabled={submitting}
        className="md:col-span-2 py-3 rounded-full font-semibold disabled:opacity-50"
        style={goldBtn}
        data-testid="kuji-event-submit-btn"
      >
        {submitting ? 'Creating...' : 'Create Draft Event'}
      </button>
    </motion.form>
  );
};

// ---------- Event Card ----------
const EventCard = ({ event, expanded, onToggle, reload }) => {
  const [prizes, setPrizes] = useState([]);
  const [showPrizeForm, setShowPrizeForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const isDraft = event.status === 'draft';
  const isActive = event.status === 'active';
  const isPaused = event.status === 'paused';
  const sc = STATUS_COLORS[event.status] || STATUS_COLORS.draft;

  const fetchPrizes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/kuji/events/${event.id}`, { withCredentials: true });
      setPrizes(data.prizes || []);
    } catch (_e) {
      // Active/sold-out are fetchable; if draft, fallback to admin endpoint? Public endpoint allows status active/paused/sold_out only.
      // Try direct list-prizes via probability snapshot or just keep empty.
      if (event.probability_snapshot) {
        setPrizes(event.probability_snapshot.map((p) => ({ ...p, id: p.prize_id })));
      } else {
        setPrizes([]);
      }
    }
  }, [event.id, event.probability_snapshot]);

  useEffect(() => {
    if (expanded) fetchPrizes();
  }, [expanded, fetchPrizes]);

  const callAction = async (action, payload = null) => {
    try {
      if (action === 'delete') {
        await axios.delete(`${API}/admin/kuji/events/${event.id}`, { withCredentials: true });
      } else if (payload) {
        await axios.post(`${API}/admin/kuji/events/${event.id}/${action}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/kuji/events/${event.id}/${action}`, {}, { withCredentials: true });
      }
      toast.success(`Event ${action}d`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const handleActivate = async () => {
    if (!window.confirm(`Activate "${event.name}"? Prizes and probability will be LOCKED. Slots pre-generated immutably.`)) return;
    await callAction('activate');
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete draft event "${event.name}"? This cannot be undone.`)) return;
    await callAction('delete');
  };

  return (
    <div className="rounded-2xl backdrop-blur-xl overflow-hidden" style={cardStyle} data-testid={`kuji-event-${event.id}`}>
      {/* Header */}
      <div className="p-5">
        <div className="flex justify-between items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-lg font-medium" style={{ color: '#F8FAF9' }}>{event.name}</h3>
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold uppercase" style={{ background: sc.bg, color: sc.fg }}>
                {event.status}
              </span>
              {event.featured && (
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37' }}>
                  FEATURED
                </span>
              )}
              {!isDraft && (
                <span className="flex items-center gap-1 text-xs" style={{ color: '#A3B8AC' }}>
                  <Lock className="w-3 h-3" /> locked
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: '#A3B8AC' }}>{event.description || '(no description)'}</p>
            <div className="flex gap-4 mt-2 text-sm flex-wrap">
              <span style={{ color: '#D4AF37' }}>💰 {fmt(event.coin_price_per_draw)} coins/draw</span>
              {!isDraft && (
                <>
                  <span style={{ color: '#A3B8AC' }}>Slots: {fmt(event.remaining_slots)}/{fmt(event.total_slots)}</span>
                  <span style={{ color: '#A3B8AC' }}>Slug: {event.slug}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {isDraft && (
              <>
                <button onClick={handleActivate} className="px-3 py-2 rounded-lg text-xs flex items-center gap-1" style={goldBtn} data-testid={`kuji-activate-${event.id}`}>
                  <Play className="w-4 h-4" /> Activate
                </button>
                <button onClick={handleDelete} className="px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }} data-testid={`kuji-delete-${event.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
            {isActive && (
              <button onClick={() => callAction('pause')} className="px-3 py-2 rounded-lg text-xs flex items-center gap-1" style={ghostBtn} data-testid={`kuji-pause-${event.id}`}>
                <Pause className="w-4 h-4" /> Pause
              </button>
            )}
            {isPaused && (
              <>
                <button onClick={() => callAction('resume')} className="px-3 py-2 rounded-lg text-xs flex items-center gap-1" style={goldBtn} data-testid={`kuji-resume-${event.id}`}>
                  <Play className="w-4 h-4" /> Resume
                </button>
                <button onClick={() => callAction('archive')} className="px-3 py-2 rounded-lg text-xs flex items-center gap-1" style={ghostBtn} data-testid={`kuji-archive-${event.id}`}>
                  <Archive className="w-4 h-4" /> Archive
                </button>
              </>
            )}
            {event.status === 'sold_out' && (
              <button onClick={() => callAction('archive')} className="px-3 py-2 rounded-lg text-xs flex items-center gap-1" style={ghostBtn} data-testid={`kuji-archive-${event.id}`}>
                <Archive className="w-4 h-4" /> Archive
              </button>
            )}
            <button onClick={onToggle} className="px-3 py-2 rounded-lg text-xs" style={ghostBtn} data-testid={`kuji-toggle-${event.id}`}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded panel */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="p-5">
              <EventMetaEditor event={event} onUpdated={reload} />

              <div className="flex justify-between items-center mt-6 mb-3 flex-wrap gap-2">
                <h4 className="text-sm font-semibold uppercase tracking-wider" style={{ color: '#D4AF37' }}>
                  Prizes ({prizes.length})
                </h4>
                <div className="flex gap-2">
                  {isDraft && (
                    <>
                      <button
                        onClick={() => { setShowPrizeForm((v) => !v); setShowBulkImport(false); }}
                        className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1"
                        style={ghostBtn}
                        data-testid={`kuji-add-prize-btn-${event.id}`}
                      >
                        <Plus className="w-3 h-3" /> Single Prize
                      </button>
                      <button
                        onClick={() => { setShowBulkImport((v) => !v); setShowPrizeForm(false); }}
                        className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1"
                        style={ghostBtn}
                        data-testid={`kuji-bulk-import-btn-${event.id}`}
                      >
                        <FileJson className="w-3 h-3" /> Bulk Import
                      </button>
                    </>
                  )}
                  {!isDraft && (
                    <button
                      onClick={() => setShowPreview((v) => !v)}
                      className="px-3 py-1.5 rounded-lg text-xs flex items-center gap-1"
                      style={ghostBtn}
                      data-testid={`kuji-preview-btn-${event.id}`}
                    >
                      <Eye className="w-3 h-3" /> {showPreview ? 'Hide' : 'Probability Snapshot'}
                    </button>
                  )}
                </div>
              </div>

              {isDraft && showPrizeForm && (
                <PrizeForm eventId={event.id} onAdded={fetchPrizes} />
              )}

              {isDraft && showBulkImport && (
                <BulkPrizeImport eventId={event.id} onDone={fetchPrizes} />
              )}

              {!isDraft && showPreview && event.probability_snapshot && (
                <ProbabilitySnapshot snapshot={event.probability_snapshot} />
              )}

              <PrizesList prizes={prizes} isDraft={isDraft} onDeleted={fetchPrizes} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ---------- Event meta editor (banner/desc/featured editable always; price/slug only when draft) ----------
const EventMetaEditor = ({ event, onUpdated }) => {
  const [form, setForm] = useState({
    name: event.name || '',
    description: event.description || '',
    banner_url: event.banner_url || '',
    cover_url: event.cover_url || '',
    featured: !!event.featured,
    featured_order: event.featured_order || 0,
    coin_price_per_draw: event.coin_price_per_draw || 0,
    slug: event.slug || '',
  });
  const [saving, setSaving] = useState(false);
  const isDraft = event.status === 'draft';

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        banner_url: form.banner_url,
        cover_url: form.cover_url,
        featured: form.featured,
        featured_order: parseInt(form.featured_order, 10) || 0,
      };
      if (isDraft) {
        payload.coin_price_per_draw = parseInt(form.coin_price_per_draw, 10);
        payload.slug = form.slug;
      }
      await axios.put(`${API}/admin/kuji/events/${event.id}`, payload, { withCredentials: true });
      toast.success('Event updated');
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-3 p-4 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)' }}>
      <input
        type="text" placeholder="Name" value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="rounded-lg px-3 py-2 outline-none" style={inputStyle}
      />
      <input
        type="text" placeholder="Slug" value={form.slug}
        onChange={(e) => setForm({ ...form, slug: e.target.value })}
        disabled={!isDraft}
        className="rounded-lg px-3 py-2 outline-none disabled:opacity-50"
        style={inputStyle}
        title={!isDraft ? 'Slug locked after activation' : ''}
      />
      <textarea
        placeholder="Description" value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        rows="2" className="md:col-span-2 rounded-lg px-3 py-2 outline-none" style={inputStyle}
      />
      <input
        type="number" placeholder="Coin Price" value={form.coin_price_per_draw}
        onChange={(e) => setForm({ ...form, coin_price_per_draw: e.target.value })}
        disabled={!isDraft}
        className="rounded-lg px-3 py-2 outline-none disabled:opacity-50"
        style={inputStyle}
        title={!isDraft ? 'Price locked after activation' : ''}
      />
      <input
        type="number" placeholder="Featured Order" value={form.featured_order}
        onChange={(e) => setForm({ ...form, featured_order: e.target.value })}
        className="rounded-lg px-3 py-2 outline-none" style={inputStyle}
      />
      <div className="md:col-span-1">
        <ImageUpload value={form.banner_url} onChange={(v) => setForm({ ...form, banner_url: v })} label="Banner" />
      </div>
      <div className="md:col-span-1">
        <ImageUpload value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} label="Cover" />
      </div>
      <label className="flex items-center gap-2 px-3 py-2 cursor-pointer md:col-span-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
        <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-yellow-500" />
        <span className="text-sm" style={{ color: '#F8FAF9' }}>Featured</span>
      </label>
      <button onClick={save} disabled={saving} className="md:col-span-2 py-2 rounded-full text-sm font-semibold disabled:opacity-50" style={goldBtn} data-testid={`kuji-save-meta-${event.id}`}>
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  );
};

// ---------- Single Prize Form ----------
const PrizeForm = ({ eventId, onAdded }) => {
  const [form, setForm] = useState({
    name: '', description: '', image_url: '', tier: 'common', quantity: 1,
    estimated_value_idr: 0, is_physical: true, digital_reward_coins: 0, buyback_coins: 0, weight_grams: 0, position: 0
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.image_url) {
      toast.error('Name and image are required');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/kuji/events/${eventId}/prizes`, {
        ...form,
        quantity: parseInt(form.quantity, 10) || 1,
        estimated_value_idr: parseInt(form.estimated_value_idr, 10) || 0,
        digital_reward_coins: parseInt(form.digital_reward_coins, 10) || 0,
        buyback_coins: parseInt(form.buyback_coins, 10) || 0,
        weight_grams: parseInt(form.weight_grams, 10) || 0,
        position: parseInt(form.position, 10) || 0,
      }, { withCredentials: true });
      toast.success('Prize added');
      setForm({ ...form, name: '', description: '', image_url: '' });
      onAdded();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Failed to add prize');
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      onSubmit={submit}
      className="grid md:grid-cols-2 gap-3 p-4 rounded-xl mb-3"
      style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)' }}
      data-testid={`kuji-prize-form-${eventId}`}
    >
      <input type="text" placeholder="Prize Name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg px-3 py-2 outline-none" style={inputStyle} data-testid="kuji-prize-name-input" />
      <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="rounded-lg px-3 py-2 outline-none" style={inputStyle} data-testid="kuji-prize-tier-select">
        <option value="gold">Gold</option>
        <option value="silver">Silver</option>
        <option value="bronze">Bronze</option>
        <option value="common">Common</option>
        <option value="last">Last (final slot)</option>
      </select>
      <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows="2" className="md:col-span-2 rounded-lg px-3 py-2 outline-none" style={inputStyle} />
      <div className="md:col-span-2">
        <ImageUpload value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} label="Prize Image *" required testId="kuji-prize-image-upload" />
      </div>
      <input type="number" min="1" placeholder="Quantity *" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="rounded-lg px-3 py-2 outline-none" style={inputStyle} data-testid="kuji-prize-qty-input" disabled={form.tier === 'last'} />
      <input type="number" placeholder="Estimated Value (IDR)" value={form.estimated_value_idr} onChange={(e) => setForm({ ...form, estimated_value_idr: e.target.value })} className="rounded-lg px-3 py-2 outline-none" style={inputStyle} />
      <input type="number" min="0" placeholder="Buyback (coins) — 0 disables sell-back" value={form.buyback_coins} onChange={(e) => setForm({ ...form, buyback_coins: e.target.value })} className="md:col-span-2 rounded-lg px-3 py-2 outline-none" style={inputStyle} data-testid="kuji-prize-buyback-input" />
      <input type="number" min="0" placeholder="Berat (gram) — wajib untuk hadiah fisik" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} className="md:col-span-2 rounded-lg px-3 py-2 outline-none" style={inputStyle} data-testid="kuji-prize-weight-input" />
      <label className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer md:col-span-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
        <input type="checkbox" checked={form.is_physical} onChange={(e) => setForm({ ...form, is_physical: e.target.checked, digital_reward_coins: e.target.checked ? 0 : form.digital_reward_coins })} className="w-4 h-4 accent-yellow-500" />
        <span style={{ color: '#F8FAF9' }} className="text-sm">Physical prize (requires shipping)</span>
      </label>
      {!form.is_physical && (
        <input type="number" placeholder="Digital Reward Coins *" required min="1" value={form.digital_reward_coins} onChange={(e) => setForm({ ...form, digital_reward_coins: e.target.value })} className="md:col-span-2 rounded-lg px-3 py-2 outline-none" style={inputStyle} />
      )}
      {form.tier === 'last' && (
        <p className="md:col-span-2 text-xs px-3" style={{ color: '#EAB308' }}>
          ⚠ Last-tier prize must be quantity=1 and will be deterministically placed at the FINAL slot.
        </p>
      )}
      <button type="submit" disabled={busy} className="md:col-span-2 py-2 rounded-full text-sm font-semibold disabled:opacity-50" style={goldBtn} data-testid="kuji-prize-submit-btn">
        {busy ? 'Adding...' : 'Add Prize'}
      </button>
    </motion.form>
  );
};

// ---------- Bulk Prize Import (JSON) ----------
const BulkPrizeImport = ({ eventId, onDone }) => {
  const [raw, setRaw] = useState('');
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const sample = JSON.stringify([
    { name: 'Common Item A', tier: 'common', quantity: 50, image_url: '/api/uploads/example.png', is_physical: true, estimated_value_idr: 50000 },
    { name: 'Bronze Trophy', tier: 'bronze', quantity: 10, image_url: '/api/uploads/example.png', is_physical: true, estimated_value_idr: 150000 },
    { name: 'Silver Charm', tier: 'silver', quantity: 5, image_url: '/api/uploads/example.png', is_physical: true, estimated_value_idr: 300000 },
    { name: 'Gold Medal', tier: 'gold', quantity: 1, image_url: '/api/uploads/example.png', is_physical: true, estimated_value_idr: 1000000 },
    { name: 'Grand Prize', tier: 'last', quantity: 1, image_url: '/api/uploads/example.png', is_physical: true, estimated_value_idr: 5000000 },
  ], null, 2);

  const run = async () => {
    let parsed;
    try {
      parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) throw new Error('JSON must be an array of prize objects');
    } catch (e) {
      toast.error('Invalid JSON: ' + e.message);
      return;
    }
    setRunning(true);
    const ok = []; const failed = [];
    for (const p of parsed) {
      try {
        await axios.post(`${API}/admin/kuji/events/${eventId}/prizes`, {
          name: p.name || '',
          description: p.description || '',
          image_url: p.image_url || '',
          tier: p.tier || 'common',
          quantity: parseInt(p.quantity, 10) || 1,
          estimated_value_idr: parseInt(p.estimated_value_idr, 10) || 0,
          is_physical: p.is_physical !== false,
          digital_reward_coins: parseInt(p.digital_reward_coins, 10) || 0,
          buyback_coins: parseInt(p.buyback_coins, 10) || 0,
          weight_grams: parseInt(p.weight_grams, 10) || 0,
          position: parseInt(p.position, 10) || 0,
        }, { withCredentials: true });
        ok.push(p.name);
      } catch (e) {
        failed.push({ name: p.name, error: e.response?.data?.detail || e.message });
      }
    }
    setReport({ ok, failed });
    setRunning(false);
    if (failed.length === 0) toast.success(`Imported ${ok.length} prizes`);
    else toast.warning(`Imported ${ok.length}; ${failed.length} failed`);
    onDone();
  };

  return (
    <div className="p-4 rounded-xl mb-3" style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.2)' }} data-testid="kuji-bulk-import-panel">
      <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
        <p className="text-sm" style={{ color: '#A3B8AC' }}>Paste a JSON array of prize objects:</p>
        <button onClick={() => setRaw(sample)} className="text-xs px-3 py-1 rounded" style={ghostBtn}>
          Insert Sample
        </button>
      </div>
      <textarea
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder='[{"name":"...","tier":"common","quantity":10,"image_url":"...","is_physical":true}]'
        rows="10"
        className="w-full rounded-lg px-3 py-2 outline-none font-mono text-xs"
        style={inputStyle}
        data-testid="kuji-bulk-import-textarea"
      />
      <button onClick={run} disabled={running} className="mt-2 px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-50 flex items-center gap-2" style={goldBtn} data-testid="kuji-bulk-import-run">
        <Upload className="w-4 h-4" /> {running ? 'Importing...' : 'Import Prizes'}
      </button>
      {report && (
        <div className="mt-3 text-xs" style={{ color: '#A3B8AC' }}>
          <p style={{ color: '#22C55E' }}>✓ Imported: {report.ok.length} ({report.ok.join(', ') || '—'})</p>
          {report.failed.length > 0 && (
            <div className="mt-1" style={{ color: '#EF4444' }}>
              ✗ Failed: {report.failed.length}
              {report.failed.map((f, i) => (
                <p key={i}>• {f.name || '(no name)'}: {f.error}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- Probability snapshot view ----------
const ProbabilitySnapshot = ({ snapshot }) => (
  <div className="mb-3 p-4 rounded-xl" style={{ background: 'rgba(0,0,0,0.25)' }} data-testid="kuji-probability-snapshot">
    <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#D4AF37' }}>Probability Snapshot (locked at activation)</p>
    <div className="space-y-2">
      {snapshot.map((p) => (
        <div key={p.prize_id} className="flex justify-between items-center text-sm">
          <span style={{ color: TIER_COLORS[p.tier] || '#F8FAF9' }}>
            [{p.tier.toUpperCase()}] {p.name}
          </span>
          <span style={{ color: '#A3B8AC' }}>
            qty {p.quantity} · {(p.probability * 100).toFixed(2)}%
          </span>
        </div>
      ))}
    </div>
  </div>
);

// ---------- Prizes Grid ----------
const PrizesList = ({ prizes, isDraft, onDeleted }) => {
  const del = async (id) => {
    if (!window.confirm('Delete this prize?')) return;
    try {
      await axios.delete(`${API}/admin/kuji/prizes/${id}`, { withCredentials: true });
      toast.success('Prize deleted');
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (prizes.length === 0) {
    return <p className="text-center py-6 text-sm" style={{ color: '#A3B8AC' }}>No prizes yet.</p>;
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kuji-prizes-grid">
      {prizes.map((pz) => (
        <div key={pz.id} className="rounded-xl p-3" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.05)' }}>
          {pz.image_url && <img src={pz.image_url} alt={pz.name} className="w-full h-24 object-cover rounded-lg mb-2" loading="lazy" />}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Trophy className="w-3 h-3" style={{ color: TIER_COLORS[pz.tier] || '#F8FAF9' }} />
            <p className="text-sm font-medium truncate" style={{ color: '#F8FAF9' }}>{pz.name}</p>
          </div>
          <p className="text-xs uppercase" style={{ color: TIER_COLORS[pz.tier] || '#A3B8AC' }}>{pz.tier}</p>
          <p className="text-xs" style={{ color: '#A3B8AC' }}>
            Qty: {pz.quantity}
            {typeof pz.probability === 'number' ? ` · ${(pz.probability * 100).toFixed(1)}%` : ''}
          </p>
          {pz.is_physical === false && pz.digital_reward_coins > 0 && (
            <p className="text-xs" style={{ color: '#D4AF37' }}>+{fmt(pz.digital_reward_coins)} coins</p>
          )}
          {isDraft && (
            <button onClick={() => del(pz.id)} className="mt-2 w-full py-1 text-xs rounded" style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }} data-testid={`kuji-delete-prize-${pz.id}`}>
              Delete
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================
//  ECONOMY TAB
// ============================================================
const EconomyTab = ({ economy }) => {
  if (!economy) return <p style={{ color: '#A3B8AC' }}>No data.</p>;
  const cards = [
    { label: 'Total Coins Consumed', value: economy.total_coins_consumed, color: '#D4AF37' },
    { label: 'Total Digital Rewards Out', value: economy.total_digital_rewards_distributed, color: '#22C55E' },
    { label: 'Total Coins Refunded', value: economy.total_coins_refunded, color: '#EAB308' },
    { label: 'Net Coin Flow', value: economy.net_coin_flow, color: economy.net_coin_flow >= 0 ? '#22C55E' : '#EF4444' },
    { label: 'Total Draws', value: economy.total_draws, color: '#F8FAF9' },
    { label: 'Unique Drawers', value: economy.unique_drawers, color: '#F8FAF9' },
    { label: 'Active Events', value: economy.active_events, color: '#22C55E' },
    { label: 'Sold-Out Events', value: economy.sold_out_events, color: '#EF4444' },
  ];

  return (
    <div data-testid="kuji-economy-dashboard">
      {economy.profitability_warning && (
        <div className="mb-4 p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
          <p style={{ color: '#EF4444' }} className="font-medium">⚠ Profitability warning: refunds + digital rewards exceed consumption.</p>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="p-4 rounded-2xl backdrop-blur-xl" style={cardStyle}>
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#A3B8AC' }}>{c.label}</p>
            <p className="text-2xl font-semibold" style={{ color: c.color, fontFamily: playfairFont }}>
              {fmt(c.value)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
//  AUDIT LOG TAB
// ============================================================
const AuditTab = ({ auditLog }) => {
  if (auditLog.length === 0) {
    return <p className="text-center py-12" style={{ color: '#A3B8AC' }}>No audit entries yet.</p>;
  }
  return (
    <div className="rounded-2xl overflow-hidden backdrop-blur-xl" style={cardStyle} data-testid="kuji-audit-log">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: 'rgba(0,0,0,0.3)' }}>
            <tr>
              <th className="text-left py-3 px-4" style={{ color: '#D4AF37' }}>Time (UTC)</th>
              <th className="text-left py-3 px-4" style={{ color: '#D4AF37' }}>Admin</th>
              <th className="text-left py-3 px-4" style={{ color: '#D4AF37' }}>Action</th>
              <th className="text-left py-3 px-4" style={{ color: '#D4AF37' }}>Target</th>
              <th className="text-left py-3 px-4" style={{ color: '#D4AF37' }}>Metadata</th>
              <th className="text-left py-3 px-4" style={{ color: '#D4AF37' }}>IP</th>
            </tr>
          </thead>
          <tbody>
            {auditLog.map((row) => (
              <tr key={row.id} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <td className="py-2 px-4 text-xs whitespace-nowrap" style={{ color: '#A3B8AC' }}>
                  {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                </td>
                <td className="py-2 px-4 text-xs" style={{ color: '#F8FAF9' }}>{row.admin_email}</td>
                <td className="py-2 px-4 text-xs font-mono" style={{ color: '#D4AF37' }}>{row.action}</td>
                <td className="py-2 px-4 text-xs" style={{ color: '#F8FAF9' }}>{row.target_type}:{row.target_id?.slice(0, 8)}</td>
                <td className="py-2 px-4 text-xs font-mono" style={{ color: '#A3B8AC' }}>{JSON.stringify(row.metadata || {})}</td>
                <td className="py-2 px-4 text-xs" style={{ color: '#A3B8AC' }}>{row.ip_address || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
//  SHIPPING TAB (Admin)
// ============================================================

const SHIPPING_STATUSES = ['pending_payment', 'paid_pending_admin', 'approved', 'shipped', 'delivered', 'cancelled'];

const SHIPPING_BADGE = {
  pending_payment: { bg: 'rgba(234,179,8,0.2)', fg: '#EAB308' },
  paid_pending_admin: { bg: 'rgba(59,130,246,0.2)', fg: '#3B82F6' },
  approved: { bg: 'rgba(34,197,94,0.2)', fg: '#22C55E' },
  shipped: { bg: 'rgba(168,85,247,0.2)', fg: '#A855F7' },
  delivered: { bg: 'rgba(34,197,94,0.3)', fg: '#22C55E' },
  cancelled: { bg: 'rgba(239,68,68,0.2)', fg: '#EF4444' },
};

const ALLOWED_TRANSITIONS = {
  pending_payment: ['paid_pending_admin', 'cancelled'],
  paid_pending_admin: ['approved', 'cancelled'],
  approved: ['shipped', 'cancelled'],
  shipped: ['delivered'],
  delivered: [],
  cancelled: [],
};

const ShippingTab = ({ items, statusFilter, setStatusFilter, reload }) => {
  const [expanded, setExpanded] = useState(null);
  return (
    <div data-testid="kuji-admin-shipping-tab">
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setStatusFilter('')} className="px-4 py-1.5 rounded-full text-xs" style={{ background: !statusFilter ? '#D4AF37' : 'rgba(255,255,255,0.05)', color: !statusFilter ? '#080C0A' : '#F8FAF9', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="shipping-filter-all">
          All
        </button>
        {SHIPPING_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className="px-4 py-1.5 rounded-full text-xs"
            style={{ background: statusFilter === s ? '#D4AF37' : 'rgba(255,255,255,0.05)', color: statusFilter === s ? '#080C0A' : '#F8FAF9', border: '1px solid rgba(255,255,255,0.1)' }}
            data-testid={`shipping-filter-${s}`}
          >
            {s.replace('_', ' ')}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 rounded-2xl backdrop-blur-xl" style={cardStyle}>
          <Truck className="w-16 h-16 mx-auto mb-4" style={{ color: '#A3B8AC' }} />
          <p style={{ color: '#A3B8AC' }}>No shipping requests yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((sr) => (
            <ShippingRow key={sr.id} sr={sr} expanded={expanded === sr.id} onToggle={() => setExpanded(expanded === sr.id ? null : sr.id)} reload={reload} />
          ))}
        </div>
      )}
    </div>
  );
};

const ShippingRow = ({ sr, expanded, onToggle, reload }) => {
  const badge = SHIPPING_BADGE[sr.status] || SHIPPING_BADGE.pending_payment;
  const allowedNext = ALLOWED_TRANSITIONS[sr.status] || [];

  const patch = async (payload) => {
    try {
      await axios.patch(`${API}/admin/kuji/shipping/requests/${sr.id}`, payload, { withCredentials: true });
      toast.success('Updated');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className="rounded-2xl backdrop-blur-xl overflow-hidden" style={cardStyle} data-testid={`admin-shipping-row-${sr.id}`}>
      <div className="p-4 flex flex-wrap items-start gap-3 cursor-pointer" onClick={onToggle}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-semibold" style={{ color: '#F8FAF9' }}>{sr.recipient_name}</span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: badge.bg, color: badge.fg }}>
              {sr.status.replace('_', ' ').toUpperCase()}
            </span>
            <span className="text-[10px]" style={{ color: '#A3B8AC' }}>{sr.user_email}</span>
          </div>
          <p className="text-xs truncate" style={{ color: '#A3B8AC' }}>{sr.address_line}{sr.district ? `, ${sr.district}` : ''}, {sr.city}, {sr.province} {sr.postal_code || ''}</p>
          <p className="text-xs mt-1" style={{ color: '#A3B8AC' }}>{sr.item_count} item(s) · {sr.courier?.toUpperCase()} {sr.service} · {fmt(sr.shipping_cost)} {sr.payment_method === 'coins' ? 'coins' : 'IDR'} · {sr.payment_method?.toUpperCase()}</p>
          {sr.tracking_number && <p className="text-xs mt-1" style={{ color: '#D4AF37' }}>Tracking: {sr.tracking_number}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {expanded ? <ChevronUp className="w-5 h-5" style={{ color: '#A3B8AC' }} /> : <ChevronDown className="w-5 h-5" style={{ color: '#A3B8AC' }} />}
        </div>
      </div>

      {expanded && <ShippingExpanded sr={sr} allowedNext={allowedNext} patch={patch} />}
    </div>
  );
};

const ShippingExpanded = ({ sr, allowedNext, patch }) => {
  const [tracking, setTracking] = useState(sr.tracking_number || '');
  const [note, setNote] = useState(sr.admin_note || '');
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/kuji/shipping/requests/${sr.id}`, { withCredentials: true })
      .then((r) => setDetail(r.data))
      .catch(() => {});
  }, [sr.id]);

  return (
    <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      {/* Items in shipment */}
      <div className="pt-3">
        <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#D4AF37' }}>Items ({sr.item_count})</p>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid={`admin-shipping-items-${sr.id}`}>
          {(detail?.inventory_items || []).map((it) => (
            <div key={it.id} className="rounded-lg overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)' }}>
              <img src={it.prize_image_snapshot} alt={it.prize_name_snapshot} className="w-full aspect-square object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
              <p className="px-2 py-1 text-[10px] truncate" style={{ color: '#F8FAF9' }}>{it.prize_name_snapshot}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Payment proof */}
      {sr.payment_proof_url && (
        <div>
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#D4AF37' }}>Payment proof</p>
          <a href={sr.payment_proof_url} target="_blank" rel="noreferrer">
            <img src={sr.payment_proof_url} alt="proof" className="max-h-40 rounded-lg" data-testid={`admin-shipping-proof-${sr.id}`} />
          </a>
        </div>
      )}

      {sr.notes && (
        <div>
          <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#D4AF37' }}>User notes</p>
          <p className="text-sm" style={{ color: '#A3B8AC' }}>{sr.notes}</p>
        </div>
      )}

      {/* Tracking + note inputs */}
      <div className="grid md:grid-cols-2 gap-2">
        <input
          type="text"
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
          placeholder="Tracking number"
          className="rounded-lg px-3 py-2 outline-none text-sm"
          style={inputStyle}
          data-testid={`admin-tracking-input-${sr.id}`}
        />
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Admin note"
          className="rounded-lg px-3 py-2 outline-none text-sm"
          style={inputStyle}
        />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        {(tracking !== (sr.tracking_number || '') || note !== (sr.admin_note || '')) && (
          <button
            onClick={() => patch({ tracking_number: tracking, admin_note: note })}
            className="px-4 py-1.5 rounded-full text-xs font-semibold"
            style={goldBtn}
            data-testid={`admin-save-tracking-${sr.id}`}
          >
            Save tracking / note
          </button>
        )}
        {allowedNext.map((next) => (
          <button
            key={next}
            onClick={() => patch({ status: next })}
            className="px-4 py-1.5 rounded-full text-xs flex items-center gap-1"
            style={next === 'cancelled' ? { background: 'rgba(239,68,68,0.2)', color: '#EF4444' } : ghostBtn}
            data-testid={`admin-status-${next}-${sr.id}`}
          >
            <CheckCircle className="w-3 h-3" /> {next.replace('_', ' ')}
          </button>
        ))}
      </div>
    </div>
  );
};

